import {
  Briefcase,
  CircleHelp,
  FileText,
  Rocket,
  Search,
  ShieldCheck,
  TriangleAlert,
  Wrench,
} from 'lucide-react';
import type { ComponentType } from 'react';

/**
 * El contenido del centro de ayuda.
 *
 * Cada guía describe lo que de verdad hay en pantalla —los nombres de los
 * botones son los de la interfaz—, y las que exigen un permiso sólo se enseñan
 * a quien lo tiene: es la misma regla que el menú (lo que no puedes hacer, no
 * se te muestra). Al cambiar una pantalla, cambiar aquí su guía.
 */
export interface Pregunta {
  /** Estable: es el ancla de la dirección (`/ayuda#subir-archivos`). */
  id: string;
  pregunta: string;
  /** Pasos numerados. */
  pasos?: string[];
  /** Texto corrido, antes de los pasos. */
  respuesta?: string;
  /** Aviso o matiz al final. */
  nota?: string;
  /** Sólo se enseña a quien tiene este permiso. */
  permiso?: string;
  /** Sólo se enseña a esta clase de rol. */
  soloAdmin?: boolean;
}

export interface Categoria {
  id: string;
  titulo: string;
  descripcion: string;
  icono: ComponentType<{ className?: string }>;
  preguntas: Pregunta[];
}

export const CATEGORIAS: Categoria[] = [
  {
    id: 'primeros-pasos',
    titulo: 'Primeros pasos',
    descripcion: 'Entrar, tu cuenta y moverte por Obralink.',
    icono: Rocket,
    preguntas: [
      {
        id: 'entrar-primera-vez',
        pregunta: '¿Cómo entro por primera vez?',
        pasos: [
          'Abre el enlace de invitación que te han dado.',
          'Elige una contraseña de al menos 12 caracteres.',
          'Ve a la pantalla de acceso y entra con tu correo y esa contraseña.',
        ],
        nota: 'El enlace sirve una sola vez y caduca a los 7 días. Si ha caducado, pide a un administrador que use «Reenviar invitación».',
      },
      {
        id: 'olvide-contrasena',
        pregunta: 'He olvidado mi contraseña',
        pasos: [
          'En la pantalla de acceso pulsa «He olvidado mi contraseña».',
          'Escribe tu correo.',
          'Abre el enlace que te llegará y elige una contraseña nueva.',
        ],
        nota: 'Al cambiarla se cierran todas tus sesiones abiertas, y tendrás que entrar de nuevo.',
      },
      {
        id: 'segundo-factor',
        pregunta: '¿Qué es el segundo factor y por qué me lo piden?',
        soloAdmin: true,
        respuesta:
          'Los administradores pueden cambiar permisos, ver la auditoría y dar de alta personas, así que su cuenta se protege con un código de 6 dígitos que cambia cada 30 segundos y sólo tu móvil conoce. Aunque alguien adivine tu contraseña, no puede entrar sin él.',
        pasos: [
          'Instala una aplicación de autenticación (Google Authenticator, Microsoft Authenticator, Authy, 1Password…).',
          'Escanea el código QR que te muestra Obralink en el primer acceso.',
          'Escribe el código de 6 dígitos y pulsa «Activar y continuar».',
          'Desde entonces, tras tu contraseña te pedirá el código.',
        ],
        nota: 'Guarda la clave que aparece en «¿No puedes escanearlo?» en tu gestor de contraseñas. Si pierdes o cambias de móvil sin ella, no podrás entrar.',
      },
      {
        id: 'usar-en-el-movil',
        pregunta: '¿Cómo uso Obralink en el móvil?',
        respuesta:
          'En pantallas pequeñas aparece abajo una barra con lo de cada día: Obras, Fotos e Incidencias.',
        pasos: [
          'Toca «Más» (abajo a la derecha) o las tres rayas (arriba a la izquierda) para abrir el menú completo.',
          'Toca el buscador de arriba para encontrar cualquier obra, archivo o persona.',
          'Las incidencias se pueden abrir aunque no haya cobertura: se envían solas al volver (ver «Incidencias»).',
        ],
      },
      {
        id: 'tema-oscuro',
        pregunta: '¿Cómo cambio entre tema claro y oscuro?',
        pasos: [
          'Pulsa tu avatar (arriba a la derecha).',
          'En «Tema» elige Claro, Oscuro o Automático (sigue el de tu aparato).',
        ],
      },
      {
        id: 'repetir-tutorial',
        pregunta: '¿Puedo volver a ver el tutorial de bienvenida?',
        pasos: [
          'Pulsa tu avatar (arriba a la derecha).',
          'Elige «Ver el tutorial». También puedes pulsar el botón de la parte de arriba de esta página.',
        ],
      },
    ],
  },

  {
    id: 'obras',
    titulo: 'Obras',
    descripcion: 'Crear obras, encontrarlas y decidir quién participa.',
    icono: Briefcase,
    preguntas: [
      {
        id: 'crear-obra',
        pregunta: '¿Cómo creo una obra?',
        permiso: 'project:create',
        pasos: [
          'Ve a «Obras» en el menú y pulsa «Nueva obra».',
          'Rellena el nombre y el tipo (son lo único obligatorio). El resto —población, fechas previstas, descripción— se puede completar después.',
          'Pulsa para crearla.',
        ],
        nota: 'Al crear la obra se genera sola su estructura de carpetas (la plantilla estándar de la empresa): no hay que montarla a mano.',
      },
      {
        id: 'encontrar-obra',
        pregunta: '¿Cómo encuentro una obra?',
        pasos: [
          'En «Obras», escribe en el buscador: busca por nombre, código, cliente o población.',
          'Usa el filtro «Estado» para ver sólo las que están en curso, terminadas, etc.',
          'Cambia entre vista de tabla y de tarjetas con los dos botones de arriba a la derecha.',
        ],
        nota: 'Desde cualquier pantalla puedes pulsar Ctrl + K (⌘ K en Mac) para buscar.',
      },
      {
        id: 'anadir-participantes',
        pregunta: '¿Cómo añado a alguien a una obra?',
        permiso: 'project:member:manage',
        pasos: [
          'Abre la obra y entra en la pestaña «Participantes».',
          'Pulsa «Añadir persona».',
          'Elige a la persona y su «Papel en la obra», y confirma.',
        ],
        nota: 'Quien sólo tiene acceso a «sus obras» ve únicamente aquellas en las que participa. Si alguien dice que no ve una obra, empieza por mirar aquí.',
      },
      {
        id: 'historial-obra',
        pregunta: '¿Dónde veo quién ha hecho qué en una obra?',
        pasos: [
          'Abre la obra y entra en la pestaña «Historial».',
          'Verás los cambios con su autor y su fecha.',
        ],
      },
    ],
  },

  {
    id: 'documentos',
    titulo: 'Documentos',
    descripcion: 'Subir, ordenar, compartir y recuperar archivos.',
    icono: FileText,
    preguntas: [
      {
        id: 'subir-archivos',
        pregunta: '¿Cómo subo archivos a una obra?',
        permiso: 'file:upload',
        pasos: [
          'Abre la obra y entra en la pestaña «Archivos».',
          'Elige la carpeta donde van (en pantallas grandes, en la lista de la izquierda).',
          'Pulsa «Subir», o arrastra los archivos sobre la lista.',
        ],
        nota: 'Los archivos grandes se suben por partes: si se corta la conexión se puede reanudar sin empezar de cero. El tamaño máximo es 2 GB por archivo.',
      },
      {
        id: 'archivo-repetido',
        pregunta: 'Me dice que ya existe un archivo con ese nombre',
        permiso: 'file:upload',
        respuesta:
          'Obralink no sobrescribe en silencio. Te pregunta qué quieres hacer: subirlo como una nueva versión del archivo existente, o descartarlo.',
        nota: 'Una nueva versión no borra la anterior: las dos se conservan, y el archivo muestra «2 versiones».',
      },
      {
        id: 'archivo-general',
        pregunta: '¿Dónde subo documentos que no son de ninguna obra?',
        permiso: 'file:upload',
        respuesta:
          'En el «Archivo general» de la empresa: plantillas, políticas, certificados, manuales… cosas que no pertenecen a una obra concreta. Lo ve toda la empresa y funciona igual que los archivos de una obra.',
        pasos: [
          'Ve a «Documentos» en el menú y abre la pestaña «Archivo general».',
          'La primera vez no hay carpetas: pulsa «Crear carpeta» y ponle nombre (por ejemplo «Políticas»).',
          'Selecciona la carpeta y pulsa «Subir», o arrastra los archivos. Para ordenar más, crea subcarpetas con el botón «Nueva carpeta», o más carpetas principales con «Nueva carpeta principal».',
        ],
        nota: 'No es una obra: no sale en el listado de Obras ni en las cifras del panel, pero sus documentos sí aparecen en «Todo lo subido» y en el buscador.',
      },
      {
        id: 'crear-carpeta',
        pregunta: '¿Cómo creo una carpeta?',
        permiso: 'folder:create',
        pasos: [
          'En «Archivos», selecciona la carpeta que va a contenerla.',
          'Pulsa el icono de carpeta con un «+» (Nueva carpeta) de la lista de carpetas.',
          'Escribe el nombre.',
        ],
      },
      {
        id: 'mover-copiar',
        pregunta: '¿Cómo muevo o copio un archivo a otra carpeta?',
        pasos: [
          'Abre el menú del archivo y elige «Mover» o «Copiar».',
          'Elige la carpeta de destino.',
          'Pulsa «Mover aquí» o «Copiar aquí».',
        ],
      },
      {
        id: 'descargar',
        pregunta: '¿Cómo descargo un archivo?',
        permiso: 'file:download',
        respuesta:
          'Pulsa el icono «Descargar» de su fila, o el botón «Descargar» al abrirlo en el visor. Los PDF e imágenes se pueden ver sin descargar.',
      },
      {
        id: 'versiones',
        pregunta: '¿Cómo veo las versiones anteriores de un archivo?',
        respuesta:
          'Selecciona el archivo: en el panel de detalle verás cuántas versiones tiene y las últimas. Cada vez que alguien sube un archivo con el mismo nombre como nueva versión, la anterior se conserva.',
      },
      {
        id: 'compartir-fuera',
        pregunta: '¿Cómo comparto un archivo con alguien de fuera de la empresa?',
        permiso: 'file:share:public',
        pasos: [
          'Abre el menú del archivo y elige «Compartir».',
          'En «Compartir fuera de la empresa» fija cuándo caduca, el máximo de descargas y, si quieres, una contraseña.',
          'Crea el enlace y pulsa «Copiar»; envíaselo a quien corresponda.',
        ],
        nota: 'Para anular un enlace antes de tiempo, en el mismo cuadro pulsa «Revocar»: deja de funcionar al instante. Los enlaces caducados aparecen marcados.',
      },
      {
        id: 'bloquear',
        pregunta: '¿Para qué sirve «Bloquear para editar»?',
        permiso: 'file:lock',
        respuesta:
          'Si vas a modificar un archivo (un plano, una hoja de cálculo) y no quieres que otra persona suba una versión a la vez, bloquéalo desde su menú. Los demás verán «Bloqueado por» y tu nombre. Desbloquéalo cuando termines.',
      },
      {
        id: 'favoritos-etiquetas',
        pregunta: '¿Cómo marco favoritos y uso etiquetas?',
        pasos: [
          'Marca la estrella de un archivo para tenerlo a mano; todos tus favoritos están en «Favoritos» (menú).',
          'Las etiquetas se asignan desde el archivo y permiten filtrar en «Archivos».',
        ],
        nota: 'Crear y borrar etiquetas de la empresa (botón de etiquetas, arriba) lo hace quien tiene permiso para gestionarlas.',
      },
      {
        id: 'recuperar-papelera',
        pregunta: 'He borrado algo por error, ¿se puede recuperar?',
        permiso: 'file:restore',
        pasos: ['Abre «Papelera» en el menú.', 'Busca el elemento y pulsa el icono «Restaurar».'],
        nota: 'Lo que borras se conserva 30 días en la papelera. Pasado ese plazo se elimina definitivamente y ya no se puede recuperar. «Eliminar definitivamente» lo hace al momento y no tiene vuelta atrás.',
      },
    ],
  },

  {
    id: 'incidencias',
    titulo: 'Incidencias, fotos y tareas',
    descripcion: 'Lo que ocurre en obra, con su prueba y su responsable.',
    icono: TriangleAlert,
    preguntas: [
      {
        id: 'abrir-incidencia',
        pregunta: '¿Cómo abro una incidencia?',
        permiso: 'incident:create',
        pasos: [
          'Ve a «Incidencias» y pulsa «Nueva incidencia».',
          'Elige la obra y escribe un asunto breve («Grieta en el forjado de planta segunda»).',
          'Cuenta qué pasa, el tipo, dónde (planta, vivienda…) y, si ya lo sabes, el responsable.',
          'Guarda. Puedes ver todas en «Listado» o moverlas por estados en «Tablero».',
        ],
      },
      {
        id: 'sin-cobertura',
        pregunta: 'No tengo cobertura en obra, ¿puedo abrir incidencias?',
        permiso: 'incident:create',
        respuesta:
          'Sí. La incidencia se guarda en tu aparato como «pendiente de enviar» y se envía sola cuando vuelve la conexión.',
        nota: 'Hasta que se envíe, no cierres sesión ni borres los datos del navegador: es donde está guardada.',
      },
      {
        id: 'subir-fotos',
        pregunta: '¿Cómo subo fotos de la obra?',
        permiso: 'photo:upload',
        pasos: [
          'Ve a «Fotografías» y pulsa «Subir fotos».',
          'Elige las fotos (desde el móvil puedes hacerlas en el momento).',
          'Indica la «Zona» (opcional, por ejemplo «Fachada norte»): sirve para comparar después.',
        ],
      },
      {
        id: 'comparar-fotos',
        pregunta: '¿Cómo comparo la evolución de una zona?',
        permiso: 'photo:read',
        respuesta:
          'En «Fotografías» pulsa «Comparar», elige la obra y la zona, y verás las fotos de esa zona en distintas fechas. Por eso conviene poner siempre la «Zona» al subirlas.',
      },
      {
        id: 'tareas',
        pregunta: '¿Dónde veo mis tareas y cómo creo una?',
        permiso: 'task:read',
        pasos: [
          '«Mis tareas» (menú) lista lo que tienes asignado.',
          '«Tareas» es la vista de todas, y se puede ver como Lista, Tablero o Calendario.',
          'Para crear una, pulsa «Nueva tarea» en esa pantalla.',
        ],
      },
    ],
  },

  {
    id: 'buscar',
    titulo: 'Buscar',
    descripcion: 'Encontrar cualquier cosa en segundos.',
    icono: Search,
    preguntas: [
      {
        id: 'buscador',
        pregunta: '¿Cómo busco una obra, un archivo o una persona?',
        pasos: [
          'Pulsa el buscador de arriba, o Ctrl + K (⌘ K en Mac) desde cualquier pantalla.',
          'Escribe unas letras: aparecen los resultados por tipo.',
          'Elige uno con el ratón o con las flechas y Intro.',
        ],
        nota: 'También busca dentro del texto de los documentos, una vez procesados (puede tardar unos minutos tras subirlos).',
      },
    ],
  },

  {
    id: 'administracion',
    titulo: 'Administración',
    descripcion: 'Personas, roles, auditoría y espacio.',
    icono: ShieldCheck,
    preguntas: [
      {
        id: 'invitar',
        pregunta: '¿Cómo doy de alta a una persona?',
        permiso: 'user:invite',
        pasos: [
          'Ve a «Usuarios» (abajo del menú) y pulsa «Invitar persona».',
          'Escribe su correo y nombre y elige su rol.',
          'La persona recibe un enlace para elegir su contraseña.',
        ],
        nota: 'Si no le llega, que mire la carpeta de spam. En «Usuarios», las invitaciones sin aceptar tienen «Reenviar invitación».',
      },
      {
        id: 'roles',
        pregunta: '¿Qué puede hacer cada rol?',
        permiso: 'user:update',
        respuesta:
          'Administrador: todo. Gerencia: aprueba certificaciones y envía presupuestos. Oficina técnica: prepara ofertas, pero no las envía. Jefe de obra: mide certificaciones y sólo ve sus obras. Encargado: incidencias y fotos desde la obra. Operario: lo mínimo para trabajar. Cliente: sólo el portal del cliente, que es otra aplicación distinta. Invitado: acceso limitado.',
        nota: 'Un buen consejo: entra una vez como Operario y otra como Cliente para ver lo que NO ven. Buena parte del diseño está ahí.',
      },
      {
        id: 'cambiar-rol',
        pregunta: '¿Cómo cambio el rol de alguien o le quito el acceso?',
        permiso: 'user:update',
        pasos: [
          'En «Usuarios», busca a la persona.',
          'Usa «Cambiar rol» para darle otro rol, o desactívala para quitarle el acceso.',
        ],
        nota: 'Desactivar a alguien no borra lo que subió ni su historial: sólo impide que entre.',
      },
      {
        id: 'auditoria',
        pregunta: '¿Cómo sé quién hizo algo y cuándo?',
        permiso: 'audit:read',
        respuesta:
          'La «Auditoría» (abajo del menú) guarda un registro de las acciones importantes, con quién las hizo, cuándo y desde dónde. Es un registro de sólo lectura: nadie puede editarlo ni borrarlo.',
      },
      {
        id: 'espacio',
        pregunta: '¿Cuánto espacio queda y cómo libero?',
        respuesta:
          'La barra «Almacenamiento» del menú muestra lo usado frente al límite de la empresa, y cambia de color cerca del tope. Para liberar, elimina lo que sobre y vacía la «Papelera»: lo que está en ella todavía cuenta.',
      },
    ],
  },

  {
    id: 'problemas',
    titulo: 'Si algo no va',
    descripcion: 'Las dudas más habituales cuando algo no sale como esperabas.',
    icono: Wrench,
    preguntas: [
      {
        id: 'no-veo-seccion',
        pregunta: 'No veo una sección que a otra persona sí le sale',
        respuesta:
          'El menú sólo enseña lo que tu rol permite. Si necesitas algo que no tienes, pídeselo a un administrador: puede cambiarte el rol desde «Usuarios».',
      },
      {
        id: 'no-veo-obra',
        pregunta: 'No encuentro una obra',
        respuesta:
          'Comprueba que no tienes puesto un filtro de «Estado» y que la buscas por su nombre o código. Si tu rol sólo ve «sus obras», necesitas estar añadido en su pestaña «Participantes».',
      },
      {
        id: 'subida-falla',
        pregunta: 'Una subida falla o se queda parada',
        pasos: [
          'Comprueba la conexión: los archivos grandes se reanudan, vuelve a intentarlo.',
          'Comprueba que no pasa de 2 GB.',
          'Mira la barra de «Almacenamiento»: si la empresa ha llegado a su límite, no se puede subir más.',
          'Comprueba que estás dentro de una carpeta (el botón «Subir» no funciona en la raíz).',
        ],
      },
      {
        id: 'cuenta-bloqueada',
        pregunta: 'Me dice «Cuenta bloqueada temporalmente»',
        respuesta:
          'Tras varios intentos fallidos de contraseña la cuenta se bloquea unos minutos para protegerla. Espera lo que indique el mensaje o usa «He olvidado mi contraseña».',
      },
      {
        id: 'codigo-no-vale',
        pregunta: 'El código del segundo factor no funciona',
        soloAdmin: true,
        respuesta:
          'Los códigos duran 30 segundos y no se pueden usar dos veces. Espera al siguiente y escríbelo sin espacios. Si sigue fallando, comprueba que la hora de tu móvil es automática: un reloj desajustado genera códigos que el servidor no acepta.',
      },
    ],
  },
];

/** Iconos de la cabecera de cada categoría cuando no hay ninguna visible. */
export const ICONO_VACIO = CircleHelp;
